import React, { useState, useEffect } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { successStories } from '@/data/successStories';
import SuccessStoryGrid from '@/components/success-story/SuccessStoryGrid';
import SuccessStoryDetail from '@/components/success-story/SuccessStoryDetail';
import SearchBar from '@/components/success-story/SearchBar';
import { Button } from '@/components/ui/button';
import { featuredAchievers } from '@/data/articles';
import ScrollToTopButton from '@/components/ui/ScrollToTopButton';
import SEO from '@/components/utils/SEO';

const SuccessStory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const achieverFilter = searchParams.get('achiever');
  
  // If storyId is provided, find the story
  const selectedStory = storyId 
    ? successStories.find(story => story.id === storyId)
    : null;
  
  // Enrich selected story with achiever data if it exists
  const enrichedStory = selectedStory && selectedStory.achieverId
    ? {
        ...selectedStory,
        // Override with achiever data if available
        ...(() => {
          const linkedAchiever = featuredAchievers.find(a => a.id === selectedStory.achieverId);
          return linkedAchiever ? {
            name: linkedAchiever.author.name,
            image: linkedAchiever.author.avatar, // Profile image
            thumbnail: selectedStory.thumbnail || linkedAchiever.imageUrl // Thumbnail image
          } : {};
        })()
      }
    : selectedStory;
  
  useEffect(() => {
    // If a storyId is provided but not found, redirect to the main success stories page
    if (storyId && !selectedStory) {
      navigate('/success-story');
    }
    
    // Scroll to top whenever storyId changes
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [storyId, selectedStory, navigate]);
  
  // Filter stories based on search term and achiever filter
  const filteredStories = successStories.filter(story => {
    // Apply search term filter
    const matchesSearchTerm = searchTerm === '' || 
      story.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      story.oneLiner.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Apply achiever filter if present
    const matchesAchiever = !achieverFilter || story.achieverId === achieverFilter;
    
    return matchesSearchTerm && matchesAchiever;
  });

  // Determine the header subtitle based on filters
  let headerSubtitle = "Inspiring journeys of individuals who transformed challenges into opportunities and built remarkable success.";
  let headerSubline = "";
  
  if (achieverFilter) {
    const achieverStories = successStories.filter(s => s.achieverId === achieverFilter);
    if (achieverStories.length > 0) {
      // Get the linked achiever's name if available
      const achieverId = achieverStories[0].achieverId;
      const linkedAchiever = achieverId ? featuredAchievers.find(a => a.id === achieverId) : null;
      const achieverName = linkedAchiever ? linkedAchiever.author.name : achieverStories[0].name;
      
      headerSubtitle = `Success stories featuring ${achieverName}. Explore their remarkable journey and achievements.`;
      headerSubline = `${achieverStories.length} success stories found`;
    }
  }

  return (
    <>
      {selectedStory ? (
        <SEO
          title={`${selectedStory.title} - Success Story`}
          description={selectedStory.oneLiner || `Discover the inspiring journey of ${selectedStory.name} and their path to success in the business world.`}
          keywords={[
            'success story',
            'entrepreneur journey',
            'business success',
            selectedStory.name,
            ...(selectedStory.fullStory?.hashtags || [])
          ]}
          type="article"
          url={`https://manamumedia.in/success-story/${selectedStory.id}`}
          image={selectedStory.thumbnail}
        />
      ) : (
        <SEO
          title="Success Stories - Manamu Media"
          description="Explore inspiring success stories from entrepreneurs and business leaders. Learn from their journeys, challenges, and achievements."
          keywords={[
            'success stories',
            'entrepreneur stories',
            'business success',
            'inspirational stories',
            'entrepreneur journey'
          ]}
        />
      )}
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <main className="flex-1 bg-gradient-to-b from-purple-50/60 to-white">
          {/* Header Section */}
          <div className="py-8 md:py-10 bg-gradient-to-r from-purple-100/50 to-amber-100/50">
            <div className="container mx-auto px-4 md:px-6">
              <h1 className="text-3xl md:text-4xl font-bold text-center mb-2">Success Stories</h1>
              <p className="text-center text-gray-600 max-w-2xl mx-auto">
                {headerSubtitle}
              </p>
              
              {headerSubline && (
                <p className="text-center text-purple-600 font-medium mt-2">{headerSubline}</p>
              )}
              
              {/* Search Controls row */}
              <div className="flex flex-col md:flex-row justify-between items-center mt-6 gap-4">
                {/* Search - only shown on the grid view */}
                {!selectedStory && (
                  <div className="w-full">
                    <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                  </div>
                )}
                
                {/* If filter is active, show clear filter button */}
                {achieverFilter && (
                  <Button 
                    variant="ghost"
                    className="text-purple-600 hover:text-purple-800"
                    onClick={() => navigate('/success-story')}
                  >
                    Clear Achiever Filter
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Single Story Detail View */}
          {enrichedStory && (
            <SuccessStoryDetail 
              story={enrichedStory} 
            />
          )}
          
          {/* Bento Grid View (shown when no specific story is selected) */}
          {!selectedStory && (
            <div className="py-8 px-4 md:px-6">
              <div className="container mx-auto">
                {/* Modern Bento Grid Layout */}
                <SuccessStoryGrid stories={filteredStories} />
                
                {/* Show a message if no stories match the filter */}
                {filteredStories.length === 0 && (
                  <div className="text-center py-10">
                    <p className="text-gray-500 text-lg">No success stories found matching your criteria.</p>
                    {achieverFilter && (
                      <button 
                        onClick={() => navigate('/success-story')}
                        className="mt-4 text-purple-600 hover:text-purple-800 font-medium"
                      >
                        View all success stories
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Add ScrollToTopButton */}
          <ScrollToTopButton />
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default SuccessStory;
